import { useEffect, useRef } from 'react';
import { rmsApi } from '../../state/axios';

const HEARTBEAT_INTERVAL_MS = 60_000; // 60 seconds

/**
 * Sends a periodic heartbeat to the presence endpoint so the backend
 * knows an active user is viewing this dashboard.  Uses setInterval
 * (not SWR) so it continues running even when the browser throttles
 * background tabs (~once per minute in Chrome).  The server-side Redis
 * TTL (default 180s) is set high enough to survive that throttling.
 */
const usePresenceHeartbeat = (dashboard: string | undefined, isLive: boolean) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!dashboard || !isLive) {
      return;
    }

    const sendHeartbeat = () => {
      rmsApi.post('/api/v1/presence/heartbeat', { dashboard }).catch(() => {
        // Silently ignore — presence is best-effort
      });
    };

    // Fire immediately on mount so presence is registered without waiting 60s
    sendHeartbeat();

    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [dashboard, isLive]);
};

export default usePresenceHeartbeat;
